// CorreoContabilidad.js — bandeja de contabilidad@binimeliz.com (14-sep-2026).
//
// Canal con el contador externo (Ricardo). Muestra lo que llegó y lo que salió
// por ese buzón, los adjuntos que el vigía guardó en Documentos y la respuesta
// del agente contador de la cuadrilla a cada correo entrante. Solo lectura:
// responderle a Ricardo se decide y se manda por correo, no desde acá.
import React, { useCallback, useEffect, useState } from "react";
import backend from "../../backend";

const ESTADO_CONSULTA = {
  pendiente:  { label: "En cola del contador", color: "#f39c12" },
  entregada:  { label: "El contador lo está revisando", color: "#3498db" },
  respondida: { label: "Respondido", color: "#2ecc71" },
};

const S = {
  th: { background: "#1a1a1a", padding: "7px 10px", textAlign: "left", fontSize: 10, fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e", textTransform: "uppercase", letterSpacing: ".04em" },
  td: { padding: "8px 10px", color: "#bbb", borderBottom: "1px solid #252525", verticalAlign: "top", fontSize: 12 },
  chip: c => ({ display: "inline-block", padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, color: c, border: `1px solid ${c}55`, background: `${c}18`, whiteSpace: "nowrap" }),
  link: { color: "#5dade2", textDecoration: "none" },
  resp: { marginTop: 6, padding: "8px 10px", background: "#161616", border: "1px solid #2a2a2a", borderRadius: 8, color: "#ddd", whiteSpace: "pre-wrap", fontSize: 12, lineHeight: 1.45 },
  muted: { color: "#777", fontSize: 11 },
};

const fmtFecha = f => {
  if (!f) return "—";
  const d = new Date(f);
  return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

const CorreoContabilidad = () => {
  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [abierto, setAbierto] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const r = await backend.get("mailEntrante?buzon=contabilidad");
      setRows(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "No se pudo cargar la bandeja");
    } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 10, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontWeight: 700, color: "#ddd" }}>📬 contabilidad@binimeliz.com</div>
          <div style={S.muted}>Canal con el contador externo. Cada correo entrante se guarda con sus adjuntos y lo revisa el agente contador; la respuesta a Ricardo la manda una persona.</div>
        </div>
        <button onClick={cargar} disabled={cargando} style={{ background: "#2a2a2a", color: "#ddd", border: "1px solid #3a3a3a", borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12 }}>
          {cargando ? "Cargando…" : "Actualizar"}
        </button>
      </div>

      {error && <div style={{ color: "#e74c3c", fontSize: 12, marginBottom: 8 }}>{error}</div>}

      {!cargando && !error && rows.length === 0 && (
        <div style={{ ...S.muted, padding: "18px 4px" }}>
          Todavía no hay correos registrados. El vigía se activa al guardar la App Password de contabilidad@ en Configuración → General, y revisa el buzón cada 5 minutos.
        </div>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr>
                <th style={S.th}>Fecha</th>
                <th style={S.th}>Dirección</th>
                <th style={S.th}>De / Para</th>
                <th style={S.th}>Asunto</th>
                <th style={S.th}>Adjuntos</th>
                <th style={S.th}>Contador</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(m => {
                const esEnviado = m.direccion === "enviado";
                const c = m.consultaRef || null;
                const ec = c ? (ESTADO_CONSULTA[c.estado] || ESTADO_CONSULTA.pendiente) : null;
                const isOpen = abierto === m._id;
                return (
                  <React.Fragment key={m._id}>
                    <tr onClick={() => setAbierto(isOpen ? null : m._id)} style={{ cursor: "pointer", background: isOpen ? "#1f1f1f" : "transparent" }}>
                      <td style={S.td}>{fmtFecha(m.fecha)}</td>
                      <td style={S.td}><span style={S.chip(esEnviado ? "#9b59b6" : "#3498db")}>{esEnviado ? "Enviado" : "Recibido"}</span></td>
                      <td style={{ ...S.td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{esEnviado ? m.destinatario : m.remitente}</td>
                      <td style={{ ...S.td, color: "#ddd" }}>{m.asunto || "(sin asunto)"}</td>
                      <td style={S.td}>
                        {(m.adjuntosGuardados || []).length === 0 && (m.adjuntos || []).length === 0 && <span style={S.muted}>—</span>}
                        {(m.adjuntosGuardados || []).map((a, i) => (
                          a.documentId && a.documentId.url
                            ? <div key={i}><a href={a.documentId.url} target="_blank" rel="noreferrer" style={S.link} onClick={e => e.stopPropagation()}>📎 {a.nombre}</a></div>
                            : <div key={i} style={S.muted}>📎 {a.nombre}</div>
                        ))}
                        {(m.adjuntosGuardados || []).length === 0 && (m.adjuntos || []).map((a, i) => <div key={i} style={S.muted}>📎 {a.nombre}</div>)}
                      </td>
                      <td style={S.td}>{ec ? <span style={S.chip(ec.color)}>{ec.label}</span> : <span style={S.muted}>{esEnviado ? "bitácora" : "—"}</span>}</td>
                    </tr>
                    {isOpen && (
                      <tr>
                        <td style={{ ...S.td, background: "#1f1f1f" }} colSpan={6}>
                          <div style={S.muted}>Resumen del correo</div>
                          <div style={{ color: "#ccc", marginTop: 4, whiteSpace: "pre-wrap" }}>{m.resumen || "(sin texto)"}</div>
                          {m.notas ? <div style={{ ...S.muted, marginTop: 6 }}>Nota: {m.notas}</div> : null}
                          {c && c.estado === "respondida" && (
                            <>
                              <div style={{ ...S.muted, marginTop: 10 }}>Respuesta del agente contador · {fmtFecha(c.respondidaEn)}</div>
                              <div style={S.resp}>{c.respuesta}</div>
                            </>
                          )}
                          {c && c.estado !== "respondida" && (
                            <div style={{ ...S.muted, marginTop: 10 }}>El agente contador todavía no responde. Maxi recibe la respuesta por Telegram apenas llega.</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CorreoContabilidad;
